import { useMemo } from 'react';

import { cn } from '~/shared/utils/cn';

interface ChartListProps {
  data: {
    name: string;
    value: number;
    percentage: number;
    dotColor: string;
  }[];
  nowrap?: boolean;
  columns?: number;
}

export function ChartStatusList({
  data,
  nowrap = true,
  columns = 2,
}: Readonly<ChartListProps>) {
  const totalItems = data.length;
  const span = 12 / columns;
  const leftOvers = totalItems % columns;
  const firstLeftoverIndex = totalItems - leftOvers;

  const itemsWithGridColumnStyle = useMemo(() => {
    return data.map((item, index) => {
      const isFirstLeftover = leftOvers > 0 && index === firstLeftoverIndex;

      const calculatedStart = isFirstLeftover
        ? Math.floor((12 - leftOvers * span) / 2 + 1)
        : 'auto';

      return {
        ...item,
        gridColumn: `${calculatedStart} / span ${span}`,
      };
    });
  }, [data, leftOvers, firstLeftoverIndex, span]);

  return (
    <div className="grid grid-cols-12 gap-md">
      {itemsWithGridColumnStyle.map(item => {
        return (
          <div
            style={{
              gridColumn: item.gridColumn,
            }}
            className={cn(
              'flex flex-col min-w-[120px] w-full justify-center items-center',
              nowrap ? ' whitespace-nowrap' : ''
            )}
            key={item.name}
          >
            <div className="flex items-center gap-sm">
              <div
                className="size-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: item.dotColor }}
              />

              <span className="text-xs text-text">{item.name}</span>
            </div>

            <div className="pl-4">
              <span className="text-base font-semibold text-text-dark">
                {item.value} | {item.percentage}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
